import { Music, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { TrackData } from './hooks/use-league-chat';
import {
  getPreferredMusicUrl,
  getUserPreferredMusicService,
  getMusicServiceInfo,
  type MusicServiceUrls,
} from '@/lib/music-service-utils';

interface MiniTrackCardProps {
  trackData: TrackData;
  className?: string;
}

export function MiniTrackCard({ trackData, className }: MiniTrackCardProps) {
  const preferredService = getUserPreferredMusicService();

  // Build URLs object for the utility function
  const urls: MusicServiceUrls = {
    spotify: trackData.spotifyUrl || undefined,
    appleMusicUrl: trackData.appleMusicUrl || undefined,
    youtubeMusicUrl: trackData.youtubeMusicUrl || undefined,
  };

  const preferredUrl = getPreferredMusicUrl(urls, preferredService);
  const serviceInfo = getMusicServiceInfo(preferredService);

  const handleOpenTrack = () => {
    if (preferredUrl) {
      window.open(preferredUrl, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <div className={`flex items-center gap-3 p-2 bg-muted/50 rounded-lg ${className || ''}`}>
      {/* Album art */}
      <div className="w-12 h-12 flex-shrink-0 rounded overflow-hidden bg-muted">
        {trackData.thumbnailUrl ? (
          <img
            src={trackData.thumbnailUrl}
            alt={trackData.title}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Music className="h-6 w-6 text-muted-foreground" />
          </div>
        )}
      </div>

      {/* Track info */}
      <div className="flex-1 min-w-0">
        <p className="font-medium text-sm truncate">{trackData.title}</p>
        <p className="text-xs text-muted-foreground truncate">{trackData.artistName}</p>
        {trackData.roundName && (
          <p className="text-[10px] text-muted-foreground/70 truncate mt-0.5">
            from {trackData.roundName}
          </p>
        )}
      </div>

      {/* Open button */}
      {preferredUrl && (
        <Button
          variant="ghost"
          size="icon"
          className="flex-shrink-0 h-8 w-8"
          onClick={handleOpenTrack}
          title={`Open in ${serviceInfo.name}`}
        >
          <ExternalLink className="h-4 w-4" />
        </Button>
      )}
    </div>
  );
}
